import { Flex } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDontShowAgain } from '~/atoms/DontShowAgain'
import { useModalState } from '~/atoms/ModalState'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { DialogDescription } from '~/components/ui/dialog'
import { useConfirmModalStyle } from '~/pages/Home/Modals/ConfirmModal/componentStyles/ConfirmModal.style'
import { Size } from '~/types/ThemeTypes'

import { Checkbox } from '@mangovoice/mango-ui'

const ConfirmModal = () => {
  const { t } = useTranslation()
  const {
    confirmModal: [confirmModalState, setConfirmModalState],
  } = useModalState()

  const [, setDontShowAgain] = useDontShowAgain()
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false)

  const styles = useConfirmModalStyle()
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const focusTargetAfterCloseRef = useRef<HTMLElement | null>(null)

  // Keeps the closing dialog's content on screen during its exit animation.
  const [shownState, setShownState] = useState(confirmModalState)
  if (confirmModalState && confirmModalState !== shownState) {
    setShownState(confirmModalState)
  }

  const title = shownState?.title ?? t('confirmModal.defaultTitle')
  const message =
    shownState?.message ?? t('confirmModal.confirmDeleteVoicemail')

  const handleConfirm = () => {
    if (!confirmModalState) return
    const { onConfirm, closeAfterConfirm = true } = confirmModalState

    if (isDontShowAgainChecked) {
      setDontShowAgain(confirmModalState?.dontShowAgainKey)
    }
    // Resolved before onConfirm: an optimistic delete can drop the row from
    // the list synchronously, after which a neighbour lookup no longer works.
    focusTargetAfterCloseRef.current =
      confirmModalState.getFocusTargetAfterConfirm?.() ?? null
    if (onConfirm) onConfirm()

    if (closeAfterConfirm) setConfirmModalState(null)
  }

  const handleCloseModal = () => {
    if (!confirmModalState) return
    const { onCancel } = confirmModalState
    focusTargetAfterCloseRef.current = null
    if (onCancel) onCancel()
    setConfirmModalState(null)
  }

  useEffect(() => {
    setIsDontShowAgainChecked(false)
  }, [confirmModalState])

  const isDestructive = shownState?.isDestructive === true
  const descriptionCss = { ...styles.bodyText, color: 'inherit' }

  return (
    <MangoModal
      open={confirmModalState !== null}
      header={title}
      role={isDestructive ? 'alertdialog' : 'dialog'}
      initialFocusEl={isDestructive ? () => cancelButtonRef.current : undefined}
      finalFocusEl={() => {
        const target = focusTargetAfterCloseRef.current
        return target?.isConnected ? target : null
      }}
      onOpenChange={(details) => {
        if (!details.open) handleCloseModal()
      }}
      size={Size.sm}
      body={
        <Flex css={styles.bodyContainer}>
          {typeof message === 'string' ? (
            <DialogDescription css={descriptionCss}>
              {message}
            </DialogDescription>
          ) : (
            <DialogDescription as="div" css={descriptionCss}>
              {message}
            </DialogDescription>
          )}
          {shownState?.dontShowAgainKey && (
            <Checkbox
              onCheckedChange={(e) =>
                setIsDontShowAgainChecked(e.checked === true)
              }
            >
              {t('confirmModal.dontAskAgain')}
            </Checkbox>
          )}
        </Flex>
      }
      footer={
        <Flex css={styles.footerContainer}>
          {shownState?.cancelText && (
            <MangoButton
              ref={cancelButtonRef}
              variant={MangoButtonVariant.Default_100}
              onClick={handleCloseModal}
            >
              {shownState?.cancelText}
            </MangoButton>
          )}
          <MangoButton
            variant={
              shownState?.isDestructive
                ? MangoButtonVariant.Destructive_300
                : MangoButtonVariant.Default_300
            }
            onClick={handleConfirm}
          >
            {shownState?.confirmText}
          </MangoButton>
        </Flex>
      }
    ></MangoModal>
  )
}

export default ConfirmModal
